import type { FounderAttentionItem, FounderPlatformHealthItem } from "../../../services/founderHomeDashboard";

export type OverviewSecurityAlert = {
  id: string;
  event_type: string;
  severity: string | null;
  message: string | null;
  reason?: string | null;
  scanner_engine?: string | null;
  security_verdict?: string | null;
  created_at: string;
  resolved?: boolean;
};

const INFRASTRUCTURE_EVENT_TYPES = new Set([
  "scanner_unavailable_publication",
  "scan_failure_blocked",
  "scan_timeout_blocked",
]);

const ACTIONABLE_EVENT_TYPES = new Set([
  "malicious_apk_upload",
  "suspicious_apk_blocked",
  "malware_rescan_detected",
  "published_app_security_suspended",
]);

const EVENT_SUMMARY: Record<string, string> = {
  malicious_apk_upload: "App blocked — malware detected",
  suspicious_apk_blocked: "App blocked — suspicious file flagged",
  malware_rescan_detected: "Published app failed a security rescan",
  published_app_security_suspended: "Published app suspended for security",
  scanner_unavailable_publication: "Publication blocked — scanner unavailable",
  scan_failure_blocked: "Publication blocked — scan error",
  scan_timeout_blocked: "Publication blocked — scan timed out",
};

function isDevelopmentTestAlert(alert: OverviewSecurityAlert): boolean {
  const haystack = [alert.scanner_engine, alert.reason, alert.message]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return (
    alert.scanner_engine === "mock_malicious" ||
    haystack.includes("mock malicious") ||
    haystack.includes("mock malicious signature")
  );
}

export function classifyOverviewSecurityAlert(alert: OverviewSecurityAlert): {
  bucket: "actionable" | "infrastructure" | "development-test" | "unknown";
  founderActionable: boolean;
} {
  if (alert.resolved) {
    return { bucket: "development-test", founderActionable: false };
  }

  if (isDevelopmentTestAlert(alert)) {
    return { bucket: "development-test", founderActionable: false };
  }

  if (INFRASTRUCTURE_EVENT_TYPES.has(alert.event_type)) {
    return { bucket: "infrastructure", founderActionable: false };
  }

  if (ACTIONABLE_EVENT_TYPES.has(alert.event_type)) {
    return { bucket: "actionable", founderActionable: true };
  }

  return { bucket: "unknown", founderActionable: false };
}

function formatAlertWhen(isoDate: string): string {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function plainLanguageDetail(alert: OverviewSecurityAlert): string {
  const when = formatAlertWhen(alert.created_at);
  const whenPhrase = when ? ` on ${when}` : "";

  switch (alert.event_type) {
    case "malicious_apk_upload":
      return `An app upload was stopped${whenPhrase} because security screening detected malware. The platform blocked publication automatically. Review the app and open security details when ready.`;
    case "suspicious_apk_blocked":
      return `An app upload was stopped${whenPhrase} because security screening flagged a suspicious file. The platform blocked publication automatically. Review the app and open security details when ready.`;
    case "malware_rescan_detected":
      return `A published app failed a routine security rescan${whenPhrase}. Review the app listing and security details.`;
    case "published_app_security_suspended":
      return `A published app was suspended${whenPhrase} after a security concern was detected. Review the app listing and security details.`;
    case "scanner_unavailable_publication":
      return `An app could not be screened because the security scanner was unavailable${whenPhrase}. Publication was blocked automatically. This is an infrastructure check, not an app malware finding.`;
    case "scan_failure_blocked":
    case "scan_timeout_blocked":
      return `An app upload was blocked${whenPhrase} because automated security screening could not finish. Publication was stopped automatically. This reflects a scanning issue, not a confirmed app threat.`;
    default:
      return alert.message?.trim() || alert.reason?.trim() || "A security-related platform event was recorded.";
  }
}

export function buildOverviewSecurityAttentionItems(
  alerts: OverviewSecurityAlert[],
): FounderAttentionItem[] {
  const actionable = alerts.filter(
    (alert) => classifyOverviewSecurityAlert(alert).founderActionable,
  );

  if (actionable.length === 0) {
    return [];
  }

  if (actionable.length === 1) {
    const alert = actionable[0];
    return [
      {
        id: `security-alert-${alert.id}`,
        label: EVENT_SUMMARY[alert.event_type] ?? "App security review needed",
        detail: plainLanguageDetail(alert),
        href: "/founder/hub/platform?tab=security",
      },
    ];
  }

  return [
    {
      id: "security-alerts-actionable",
      label: `${actionable.length} app security reviews needed`,
      detail:
        "Apps were blocked or suspended after automated security screening. Review the affected apps and open security details.",
      href: "/founder/hub/platform?tab=security",
    },
  ];
}

export function buildOverviewSecurityHealthItem(
  alerts: OverviewSecurityAlert[],
  fallback: FounderPlatformHealthItem,
): FounderPlatformHealthItem {
  const actionable = alerts.filter(
    (alert) => classifyOverviewSecurityAlert(alert).founderActionable,
  );
  const infrastructure = alerts.filter(
    (alert) => classifyOverviewSecurityAlert(alert).bucket === "infrastructure",
  );

  if (actionable.length > 0) {
    return {
      ...fallback,
      status: "attention",
      detail:
        actionable.length === 1
          ? "One app security review may be needed"
          : `${actionable.length} app security reviews may be needed`,
    };
  }

  if (infrastructure.length > 0) {
    return {
      ...fallback,
      status: "healthy",
      detail: "Scanner checks are operating — recent publication blocks were automatic",
    };
  }

  return {
    ...fallback,
    status: "healthy",
    detail: "No open app security reviews",
  };
}

export function summarizeOverviewSecurityAlerts(alerts: OverviewSecurityAlert[]): {
  source: "REAL CURRENT" | "DEVELOPMENT-TEST" | "HISTORICAL" | "NON-ACTIONABLE" | "UNKNOWN";
  founderActionable: boolean;
  trace: string;
} {
  if (alerts.length === 0) {
    return {
      source: "UNKNOWN",
      founderActionable: false,
      trace:
        "GET /founder/security/alerts?resolved=false → admin_security_alerts WHERE resolved = FALSE returned no rows.",
    };
  }

  const classifications = alerts.map((alert) => ({
    alert,
    ...classifyOverviewSecurityAlert(alert),
  }));

  const actionable = classifications.filter((item) => item.founderActionable);
  const development = classifications.filter((item) => item.bucket === "development-test");
  const infrastructure = classifications.filter((item) => item.bucket === "infrastructure");

  const sample = alerts[0];
  const trace =
    `GET /founder/security/alerts?resolved=false → admin_security_alerts ` +
    `(event_type=${sample.event_type}, scanner_engine=${sample.scanner_engine ?? "—"}, ` +
    `resolved=${sample.resolved ?? false}, created_at=${sample.created_at})`;

  if (development.length === alerts.length) {
    return { source: "DEVELOPMENT-TEST", founderActionable: false, trace };
  }

  if (actionable.length > 0) {
    return { source: "REAL CURRENT", founderActionable: true, trace };
  }

  if (infrastructure.length === alerts.length) {
    return { source: "NON-ACTIONABLE", founderActionable: false, trace };
  }

  return { source: "UNKNOWN", founderActionable: false, trace };
}
